import React, { useEffect, useState } from "react";
import {
  PayoutInterface,
  PayoutStatusInterface,
} from "../../../interfaces/payout.interface";
import { get, post } from "../../../redux/ApiHelper";
import { format, isValid } from "date-fns";

const PayoutTransactionStatusMore = ({ row }: { row: PayoutInterface }) => {
  const [payoutStatus, setPayoutStatus] = useState<PayoutStatusInterface>({
    total: 0,
    transactions: [],
    isSuccess: false,
    loading: false,
  });

  const [refreshingTxId, setRefreshingTxId] = useState<string | null>(null);

  const refreshAll = () => {
    setPayoutStatus((prev) => ({ ...prev, loading: true }));
    get(`/admin/payout/request-status-payout/${row._id}`)
      .then((res) => {
        setPayoutStatus((prev) => ({
          ...prev,
          transactions: res.transactions,
          isSuccess: res.isSuccess,
          total: res.total,
        }));
      })
      .catch(console.error)
      .finally(() => {
        setPayoutStatus((prev) => ({ ...prev, loading: false }));
      });
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "SUCCESS":
        return "text-green-600";
      case "PENDING":
      case "APPROVAL_PENDING":
        return "text-yellow-600";
      case "FAILED":
      case "REJECTED":
        return "text-red-600";
      default:
        return "text-gray-500";
    }
  };

  const refreshSingleTransaction = async (
    txId: string,
    transfer_id: string,
    cf_transfer_id: string
  ) => {
    setRefreshingTxId(txId); // Start per-row loading
    try {
      const res = await post(`/admin/payout/request-status-cashfree`, {
        payoutId: row._id,
        transfer_id,
        cf_transfer_id,
      });

      setPayoutStatus((prev) => ({
        ...prev,
        transactions: prev.transactions.map((tx) =>
          tx._id === txId
            ? {
                ...tx,
                status: res.payoutTransaction.status,
                message: res.payoutTransaction.message,
              }
            : tx
        ),
      }));
    } catch (err) {
      console.error("Refresh failed:", err);
    } finally {
      setRefreshingTxId(null); // Stop per-row loading
    }
  };

  return (
    <div className="p-4 overflow-x-auto">
      {payoutStatus.loading ? (
        <p className="text-sm text-gray-500 mb-2">Fetching transactions...</p>
      ) : null}

      {payoutStatus.transactions.length > 0 ? (
        <table className="min-w-full border border-gray-200 divide-y divide-gray-200 shadow-sm">
          <thead className="bg-gray-100 text-left">
            <tr>
              <th className="px-4 py-2 text-sm font-medium text-gray-700">#</th>
              <th className="px-4 py-2 text-sm font-medium text-gray-700">
                Transfer ID
              </th>
              <th className="px-4 py-2 text-sm font-medium text-gray-700">
                CF Transfer ID
              </th>
              <th className="px-4 py-2 text-sm font-medium text-gray-700">
                UTR
              </th>
              <th className="px-4 py-2 text-sm font-medium text-gray-700">
                Date
              </th>
              <th className="px-4 py-2 text-sm font-medium text-gray-700">
                Status
              </th>
              <th className="px-4 py-2 text-sm font-medium text-gray-700">
                Message
              </th>
              <th className="px-4 py-2 text-sm font-medium text-gray-700">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {payoutStatus.transactions.map((tx, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm text-gray-500">{index + 1}</td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {tx.transfer_id}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {tx.cf_transfer_id}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {tx.transfer_utr || "N/A"}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {tx.createdAt && isValid(new Date(tx.createdAt))
                    ? format(new Date(tx.createdAt), "dd MMM yyyy, hh:mm a")
                    : "N/A"}
                </td>
                <td
                  className={`px-4 py-2 text-sm font-medium ${getStatusColor(
                    tx.status
                  )}`}
                >
                  {tx.status || "Unknown"}
                </td>
                <td className="px-4 py-2 text-sm text-gray-600">
                  {tx.message || "—"}
                </td>
                <td className="px-4 py-2">
                  <button
                    onClick={() =>
                      refreshSingleTransaction(
                        tx._id as string,
                        tx.transfer_id,
                        tx.cf_transfer_id
                      )
                    }
                    disabled={refreshingTxId === tx._id}
                    className={`text-xs px-2 py-1 rounded ${
                      refreshingTxId === tx._id
                        ? "bg-gray-400 text-white cursor-not-allowed"
                        : "bg-blue-500 text-white hover:bg-blue-600"
                    }`}
                  >
                    {refreshingTxId === tx._id ? "Refreshing..." : "Refresh"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : !payoutStatus.loading ? (
        <p className="text-sm text-gray-500">No transactions found.</p>
      ) : null}
    </div>
  );
};

export default PayoutTransactionStatusMore;
